import {
  Box,
  Button,
  TextField,
  Typography,
  useTheme,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Snackbar,
  Alert,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useState } from "react";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

const PLANS = ["Starter", "Standard", "Premium", "Enterprise"];

const AddSchool = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [form, setForm] = useState({
    schoolName: "",
    location: "",
    contactPerson: "",
    email: "",
    phone: "",
    plan: "Starter",
  });

  const [generatedLink, setGeneratedLink] = useState("");
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    const { schoolName, location, contactPerson, email, phone } = form;
    if (!schoolName || !location || !contactPerson || !email || !phone) {
      setSnackbar({ open: true, message: "Please fill in all fields.", severity: "error" });
      return;
    }

    setLoading(true);
    try {
      const response = await fetch("http://64.225.78.248:3001/api/schools", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          school_name: schoolName,
          location,
          contact_person: contactPerson,
          email,
          phone,
          plan: form.plan,
        }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error);

      setGeneratedLink(data.onboarding_link);
      setSubmitted(true);
      setSnackbar({ open: true, message: "School created! Onboarding link generated.", severity: "success" });
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const handleReset = () => {
    setForm({
      schoolName: "",
      location: "",
      contactPerson: "",
      email: "",
      phone: "",
      plan: "Starter",
    });
    setGeneratedLink("");
    setSubmitted(false);
    setCopied(false);
  };

  return (
    <Box m="20px">
      <Header title="ADD SCHOOL" subtitle="Create a new school client and generate their onboarding link" />

      <Box
        mt="30px"
        display="grid"
        gridTemplateColumns="repeat(2, 1fr)"
        gap="20px"
        sx={{ maxWidth: "860px" }}
      >
        <TextField
          fullWidth
          label="School Name"
          name="schoolName"
          value={form.schoolName}
          onChange={handleChange}
          variant="filled"
          disabled={submitted}
          sx={{ gridColumn: "span 2" }}
        />

        <TextField
          fullWidth
          label="Location / City"
          name="location"
          value={form.location}
          onChange={handleChange}
          variant="filled"
          disabled={submitted}
        />

        <FormControl fullWidth variant="filled" disabled={submitted}>
          <InputLabel>Subscription Plan</InputLabel>
          <Select name="plan" value={form.plan} onChange={handleChange}>
            {PLANS.map((plan) => (
              <MenuItem key={plan} value={plan}>{plan}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          fullWidth
          label="Contact Person"
          name="contactPerson"
          value={form.contactPerson}
          onChange={handleChange}
          variant="filled"
          disabled={submitted}
        />

        <TextField
          fullWidth
          label="Phone Number"
          name="phone"
          value={form.phone}
          onChange={handleChange}
          variant="filled"
          disabled={submitted}
        />

        <TextField
          fullWidth
          label="Admin Email"
          name="email"
          value={form.email}
          onChange={handleChange}
          variant="filled"
          disabled={submitted}
          sx={{ gridColumn: "span 2" }}
        />

        {!submitted && (
          <Box sx={{ gridColumn: "span 2" }}>
            <Button
              onClick={handleSubmit}
              variant="contained"
              fullWidth
              disabled={loading}
              sx={{
                backgroundColor: colors.greenAccent[600],
                color: "#fff",
                fontWeight: "bold",
                fontSize: "15px",
                padding: "12px",
                "&:hover": { backgroundColor: colors.greenAccent[500] },
              }}
            >
              {loading ? "Creating School & Provisioning Database..." : "Create School & Generate Link"}
            </Button>
          </Box>
        )}
      </Box>

      {submitted && generatedLink && (
        <Box
          mt="30px"
          p="20px"
          borderRadius="8px"
          sx={{ backgroundColor: colors.primary[400], maxWidth: "860px" }}
        >
          <Box display="flex" alignItems="center" gap="10px" mb="10px">
            <CheckCircleOutlineIcon sx={{ color: colors.greenAccent[400], fontSize: "26px" }} />
            <Typography variant="h5" color={colors.greenAccent[400]} fontWeight="bold">
              School Created & Database Provisioned
            </Typography>
          </Box>

          <Typography variant="body2" color={colors.grey[300]} mb="12px">
            Share this link with the school admin. They will use it to sign up and access their AMS instance.
          </Typography>

          <Box
            display="flex"
            alignItems="center"
            gap="10px"
            p="12px"
            borderRadius="6px"
            sx={{ backgroundColor: colors.primary[500] }}
          >
            <Typography
              variant="body2"
              color={colors.blueAccent[300]}
              sx={{ wordBreak: "break-all", flex: 1 }}
            >
              {generatedLink}
            </Typography>
            <Tooltip title={copied ? "Copied!" : "Copy Link"}>
              <IconButton onClick={handleCopy} sx={{ color: colors.greenAccent[400] }}>
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
          </Box>

          <Box display="flex" gap="12px" mt="20px">
            <Button
              variant="outlined"
              onClick={handleReset}
              sx={{
                borderColor: colors.blueAccent[400],
                color: colors.blueAccent[300],
                "&:hover": { borderColor: colors.blueAccent[300] },
              }}
            >
              Add Another School
            </Button>
            <Button
              variant="contained"
              href="/schools"
              sx={{
                backgroundColor: colors.blueAccent[600],
                color: "#fff",
                "&:hover": { backgroundColor: colors.blueAccent[500] },
              }}
            >
              View All Schools
            </Button>
          </Box>
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AddSchool;
